import classNames from 'classnames';
import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { Checkbox } from 'semantic-ui-react';
import { PassengerField, PassengerTypeKey } from 'Models/Enums';
import InputCounter from 'Views/Components/_HumanWritten/PassengerAmount/InputCounter';
import If from 'Views/Components/If/If';
import { UpdateWizardData } from './FerryFilterPassengers';
import { ensureDriverAssigned, setDriverPassengerType } from 'Util/_HumanWritten/DriverAssignment';
import { isNotNullOrUndefined, isNullOrUndefined, stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { passengerRemovalAlwaysAsks, RenderPassengerRemovalModal } from './Modals/PassengerRemovalModal';
import {
	BookingWizardData,
	BookingWizardInnerDataType,
	getPassengerTickets,
	setPassengerTickets,
} from '../../../BookingWizardData/BaseBookingWizardData';
import { assignDriverDetailsToPassenger, PassengerDetail } from '../../../BookingWizardData/PassengerDetail';
import { useBookingWizardDataStore } from '../../../BookingWizardDataStore';
import { PassengerBookingWizardData } from '../../../BookingWizardData/PassengerBookingWizardData';
import { VehicleBookingWizardData } from '../../../BookingWizardData/VehicleBookingWizardData';
import { WizardErrors } from '../../../BookingWizardTypes';
import { Location } from 'react-router-dom';

export interface PassengerTypeInputProps {
	location: Location;
}

/**
 * The driver helpers work over the eight ticket arrays, which freight data does not have. Freight
 * carries no passengers, so "no source" is the honest answer rather than a cast — and every helper
 * in `DriverAssignment` already treats an absent source as nothing to do.
 */
function passengerSource(innerWizardData: BookingWizardInnerDataType) {
	return innerWizardData.type === 'freight' ? undefined : innerWizardData;
}

export function PassengerTypeInputs({ location }: PassengerTypeInputProps) {
	const { wizardData, oldWizardData, setWizardData, innerWizardErrors, setInnerWizardErrors } =
		useBookingWizardDataStore();

	const inBookingWizard = location.pathname.includes('booking-wizard');

	const isVehicleBooking = wizardData.innerWizardData.type === 'vehicle';

	const { hasPassengers: oldWizardDataHasPassengers } = checkPassengersAndDriver(oldWizardData ?? undefined);

	const canNotEditPassengerCount =
		wizardData.wizardMode === 'ALTERATION' &&
		!oldWizardDataHasPassengers &&
		appConfigStore.DriverlessVehicleBookingPriceIncludesAdultPrice;

	/*
		Gives a vehicle booking a driver when it has none. It needs to exist — the reservation step
		refuses a vehicle booking with passengers and nobody driving — but it caused #2765, so the
		conditions matter more than the body.

		The deps were `[]`. On the edit route this component mounts before
		`BookingWizardWrapInner`'s fetch resolves, so it ran against the *default* wizard data, could
		not tell "this booking has no driver" from "this booking has not arrived yet", and flagged an
		adult. The customer in #2765 had a student/senior driver, and the fare moved by $6.25 because
		the driver's passenger type is what the measurement filter reads.

		So: wait until there is something real to look at (`bookingToEdit` empty means a new booking,
		nothing to wait for; otherwise `departingJourney` is populated once the booking has loaded),
		and repair at most once per booking.
	*/
	const repairedDriverFor = React.useRef<string | null>(null);

	React.useEffect(() => {
		if (!isVehicleBooking) {
			return;
		}

		const bookingId = wizardData.bookingToEdit ?? '';
		const bookingHasLoaded = bookingId === '' || isNotNullOrUndefined(wizardData.departingJourney);
		if (!bookingHasLoaded || repairedDriverFor.current === bookingId) {
			return;
		}

		const { hasPassengers, hasDriver } = checkPassengersAndDriver(wizardData);

		// `hasPassengers`, not `requiresDriver`: a vehicle booking with no passengers at all is a
		// driverless booking, which is legitimate and must not acquire a driver here.
		if (hasPassengers && !hasDriver) {
			const newData = new BookingWizardData({ ...wizardData });
			reassignDriver(newData);
			setWizardData(newData);
		}

		repairedDriverFor.current = bookingId;
	}, [isVehicleBooking, wizardData, setWizardData]);

	return (
		<>
			<div className={classNames('passenger-counter__wrapper')}>
				{passengerTypeStore.passengerTypes.map(x => {
					const key = x.passengerTypeKey;
					const propertyName = passengerTypeStore.getWizardDataProperty(key);

					const title = passengerTypeStore.getPluralName(key);
					const subtitle = passengerTypeStore.getDescription(key);
					const passengerTypeQuantity = passengerTypeStore.getQuantity(key);
					const amount =
						(getPassengerTickets(wizardData.innerWizardData, propertyName)?.length ?? 0) /
						passengerTypeQuantity;
					const tooltip = passengerTypeStore.getTooltip(key);
					const subtractDisabled = isSubtractDisabled(wizardData, key, oldWizardData);

					const hasDriver = getPassengerTickets(wizardData.innerWizardData, propertyName)?.some(
						(p: PassengerDetail) => p.driver,
					);
					const canBeDriver = passengerTypeStore.getCanBeDriver(key);

					return (
						<div className="input-counter__wrapper">
							<InputCounter
								key={x.id}
								className={`${key} ${(canBeDriver || hasDriver) && amount > 0 ? 'driver' : ''}`}
								title={title}
								subtitle={subtitle}
								tooltip={tooltip}
								amount={amount}
								subtractDisabled={subtractDisabled || canNotEditPassengerCount}
								onSubtract={() =>
									onPassengerSubtract(
										wizardData,
										setWizardData,
										innerWizardErrors,
										setInnerWizardErrors,
										key,
									)
								}
								onAdd={() =>
									onPassengerAdd(
										wizardData,
										setWizardData,
										innerWizardErrors,
										setInnerWizardErrors,
										key,
									)
								}
								addDisabled={canNotEditPassengerCount}
							/>
							{/*
								`isVehicleBooking` gates both rows, not just `Set as driver`.

								Only a vehicle booking has a driver, and the flag is now deliberately
								kept in memory across a switch to walk-on — that is what lets switching
								back restore the booking's original driver instead of handing the role
								to the first eligible passenger type. The cost is that a walk-on
								booking can be holding a dormant flag, and `Driver included` used to
								render on `hasDriver` alone, so it appeared on a booking carrying no
								vehicle. The flag never reached the server (it is stripped at submit,
								with `ApplyDriverFlag` behind that), so this was always a display
								fault — but a misleading one.
							*/}
							<If condition={canBeDriver && amount > 0 && inBookingWizard && isVehicleBooking}>
								<If condition={!hasDriver}>
									<div
										className={classNames('driver__checkbox', { selected: hasDriver })}
										onClick={() => {
											if (!hasDriver) {
												onSetAsDriver(wizardData, setWizardData, key);
											}
										}}
										onKeyDown={() => {}}
									>
										<Checkbox checked={false} onChange={() => {}} />
										Set as driver
									</div>
								</If>
								<If condition={hasDriver}>
									<div
										className={classNames('driver__checkbox', { selected: hasDriver })}
										onClick={() => {
											if (!hasDriver) {
												onSetAsDriver(wizardData, setWizardData, key);
											}
										}}
										onKeyDown={() => {}}
									>
										<FormattedMessage
											id="passenger-type-selection__driver-included"
											defaultMessage="Driver included"
										/>
									</div>
								</If>
							</If>
						</div>
					);
				})}
			</div>
		</>
	);
}

function isSubtractDisabled(
	wizardData: BookingWizardData,
	key: PassengerTypeKey,
	oldWizardData: BookingWizardData | null,
) {
	const { tabSelected } = wizardData;
	const propertyName = passengerTypeStore.getWizardDataProperty(key);
	const passengerCount = getPassengerTickets(wizardData.innerWizardData, propertyName)?.length ?? 0;

	if (wizardData.restrictedMode && oldWizardData) {
		const oldPassengerCount = getPassengerTickets(oldWizardData.innerWizardData, propertyName)?.length ?? 0;
		return oldPassengerCount === passengerCount;
	}

	return passengerCount <= 0;
}

function onPassengerSubtract(
	wizardData: BookingWizardData,
	onUpdateFilters: UpdateWizardData,
	innerWizardErrors: WizardErrors<BookingWizardInnerDataType>,
	setInnerWizardErrors: (errors: WizardErrors<BookingWizardInnerDataType>) => void,
	key: PassengerTypeKey,
) {
	const newData = new BookingWizardData({ ...wizardData });

	const propertyName = passengerTypeStore.getWizardDataProperty(key);
	const passengers = getWizardDataPassengerTickets(newData, propertyName);
	const passengerTypeCount = passengerTypeStore.getQuantity(key);

	// Check if we're removing the driver
	let removingDriver = false;
	const passengersToRemove = passengers.slice(-passengerTypeCount);
	removingDriver = passengersToRemove.some(p => p.driver);

	// We will automatically remove passengers if they have no data assigned to them
	const filteredPassengers = passengers.filter(x => {
		return (
			stringIsEmpty(x.firstName) &&
			stringIsEmpty(x.lastName) &&
			stringIsEmpty(x.phone) &&
			stringIsEmpty(x.email) &&
			stringIsEmpty(x.address) &&
			stringIsEmpty(x.gender) &&
			isNullOrUndefined(x.age) &&
			isNullOrUndefined(x.dateOfBirth)
		);
	});

	// Grouped types (quantity > 1) come off as a whole group, so there is no single passenger to pick.
	const mustChoose = passengerRemovalAlwaysAsks()
		? passengers.length > 0
		: filteredPassengers.length === 0 && passengers.length > 1;

	if (mustChoose && passengerTypeCount === 1) {
		// Do a pop-up here to search through the passengers and choose who to remove
		RenderPassengerRemovalModal(passengers).then(id => {
			if (stringNotEmpty(id)) {
				const passengerToRemove = passengers.find(y => y.id === id);
				const wasDriver = passengerToRemove?.driver ?? false;

				setPassengerTickets(
					newData.innerWizardData,
					propertyName,
					passengers.filter(y => y.id !== id),
				);

				// If we removed the driver, reassign to another eligible passenger
				if (wasDriver && wizardData.tabSelected === 'vehicle') {
					reassignDriver(newData);
				}

				onUpdateFilters(newData);
			}
		});
	} else {
		// Remove the last item
		for (let i = 0; i < passengerTypeCount; i++) {
			passengers.pop();
		}
		setPassengerTickets(newData.innerWizardData, propertyName, passengers);

		// If we removed the driver, reassign to another eligible passenger
		if (removingDriver && wizardData.tabSelected === 'vehicle') {
			reassignDriver(newData);
		}

		onUpdateFilters(newData);
	}

	// Clear the errors
	const newErrors = { ...(innerWizardErrors as WizardErrors<VehicleBookingWizardData | PassengerBookingWizardData>) };
	delete newErrors.adultTickets;
	setInnerWizardErrors(newErrors);
}

/**
 * Hands the driver role to someone when the booking has nobody.
 *
 * Delegates to `ensureDriverAssigned` rather than searching here. This used to be its own copy of
 * that search, and the copies drifted: this one scanned the *enabled* passenger types only, so a
 * booking whose driver sat on a type the operator had since disabled read as driverless and got a
 * second driver flagged on an enabled type. `ensureDriverAssigned` is also a no-op when a driver
 * already exists, which this had no way of checking.
 */
function reassignDriver(wizardData: BookingWizardData) {
	if (ensureDriverAssigned(passengerSource(wizardData.innerWizardData))) {
		assignDriverDetailsToPassenger(wizardData);
	}
}

function onPassengerAdd(
	wizardData: BookingWizardData,
	onUpdateFilters: UpdateWizardData,
	innerWizardErrors: WizardErrors<BookingWizardInnerDataType>,
	setInnerWizardErrors: (errors: WizardErrors<BookingWizardInnerDataType>) => void,
	key: PassengerTypeKey,
) {
	const newData = new BookingWizardData({ ...wizardData });

	const propertyName = passengerTypeStore.getWizardDataProperty(key);
	const passengers = getWizardDataPassengerTickets(newData, propertyName);
	const passengerTypeCount = passengerTypeStore.getQuantity(key);

	for (let i = 0; i < passengerTypeCount; i++) {
		passengers.push(new PassengerDetail({ key: key }));
	}
	setPassengerTickets(newData.innerWizardData, propertyName, passengers);

	// The first passenger added to a vehicle booking becomes its driver. `ensureDriverAssigned`
	// returns false when one already exists, so adding a second passenger cannot take the role off
	// whoever has it — which the hand-rolled check this replaced got right only for enabled
	// passenger types.
	if (wizardData.tabSelected === 'vehicle' && ensureDriverAssigned(passengerSource(newData.innerWizardData))) {
		assignDriverDetailsToPassenger(newData);
	}

	onUpdateFilters(newData);

	// Clear the errors
	const newErrors = { ...(innerWizardErrors as WizardErrors<VehicleBookingWizardData | PassengerBookingWizardData>) };
	delete newErrors.adultTickets;
	setInnerWizardErrors(newErrors);
}

function getWizardDataPassengerTickets(wizardData: BookingWizardData, propertyName: PassengerField): PassengerDetail[] {
	return getPassengerTickets(wizardData.innerWizardData, propertyName) ?? [];
}

/**
 * The user picked a passenger type to drive. One of that type's passengers takes the role, and
 * whoever held it loses it.
 *
 * `setDriverPassengerType` clears before it sets, over *every* passenger type. The clear loop this
 * replaced ran over the enabled types only, so choosing a new driver could not take the flag off a
 * passenger whose type had since been disabled — and the booking was saved carrying two.
 */
function onSetAsDriver(wizardData: BookingWizardData, onUpdateFilters: UpdateWizardData, key: PassengerTypeKey) {
	const newData = new BookingWizardData({ ...wizardData });

	if (setDriverPassengerType(passengerSource(newData.innerWizardData), key)) {
		assignDriverDetailsToPassenger(newData);
	}

	onUpdateFilters(newData);
}

export function checkPassengersAndDriver(wizardData?: BookingWizardData): {
	hasPassengers: boolean;
	requiresPassengers: boolean;
	hasDriver: boolean;
	requiresDriver: boolean;
} {
	let hasPassengers = false;
	let hasDriver = false;

	if (isNullOrUndefined(wizardData)) {
		return {
			hasPassengers,
			requiresPassengers: false,
			hasDriver,
			requiresDriver: false,
		};
	}

	if (wizardData.innerWizardData.type === 'freight') {
		return {
			hasPassengers: false,
			requiresPassengers: false,
			hasDriver: false,
			requiresDriver: false,
		};
	}

	// Every passenger type, disabled included — this counts what is on the booking rather than what
	// the sidebar offers. Reading the enabled list told the caller a booking was driverless when its
	// driver sat on a disabled type, and the repair below then flagged a second one.
	passengerTypeStore.allPassengerTypes.forEach(passengerType => {
		const propertyName = passengerTypeStore.getWizardDataProperty(passengerType.passengerTypeKey);
		const passengers = getPassengerTickets(wizardData.innerWizardData, propertyName);

		if (passengers && passengers.length > 0) {
			hasPassengers = true;

			// Check if any passenger in this type is a driver
			if (passengers.some(p => p.driver)) {
				hasDriver = true;
			}
		}
	});

	// If it is a vehicle booking and booking requires at least 1 passenger (no driverless bookings)
	// OR there are passengers on the booking but none of them are a driver (meaning it isn't a driverless vehicle)
	const requiresDriver =
		wizardData.innerWizardData.type === 'vehicle' &&
		(appConfigStore.vehicleBookingRequiresAdult || (hasPassengers && !hasDriver));
	const requiresPassengers = requiresDriver || wizardData.innerWizardData.type === 'passenger';

	return {
		hasPassengers,
		requiresPassengers,
		hasDriver,
		requiresDriver,
	};
}
